import { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, Button, Input, Badge } from '../../components/ui';
import {
  Upload,
  X,
  FileText,
  CheckCircle,
  AlertCircle,
  AlertTriangle,
  Sparkles,
  ChevronLeft,
} from 'lucide-react';
import { documentService, type DocumentStatus } from '../../services/documents';
import { submissionService, type ExtractedMetadata, type CompletenessAssessment, type TeamMember } from '../../services/submissions';
import { programsAPI, type Program } from '../../lib/services';

interface UploadedFile {
  id: string | number;
  documentId?: number;
  name: string;
  size: string;
  type: string;
  status: 'uploading' | 'processing' | 'ready' | 'error';
  error?: string;
}

const NewSubmission = () => {
  const navigate = useNavigate();
  const { programId } = useParams<{ programId: string }>();
  const [dragActive, setDragActive] = useState(false);
  const [uploadedFiles, setUploadedFiles] = useState<UploadedFile[]>([]);
  const [processing, setProcessing] = useState(false);
  const [submissionId, setSubmissionId] = useState<number | null>(null);
  const [loadingSubmission, setLoadingSubmission] = useState(true);
  const [program, setProgram] = useState<Program | null>(null);
  const [loadingProgram, setLoadingProgram] = useState(true);
  
  // Form data
  const [companyName, setCompanyName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [companyAddress, setCompanyAddress] = useState('');
  const [linkedinUrl, setLinkedinUrl] = useState('');
  const [website, setWebsite] = useState('');

  // AI metadata extraction
  const [extractingMetadata, setExtractingMetadata] = useState(false);
  const [extractedMetadata, setExtractedMetadata] = useState<ExtractedMetadata | null>(null);
  const [showCompanyInfo, setShowCompanyInfo] = useState(false);
  const [metadataError, setMetadataError] = useState<string | null>(null);

  // AI completeness assessment
  const [assessing, setAssessing] = useState(false);
  const [assessment, setAssessment] = useState<CompletenessAssessment | null>(null);
  const [assessmentError, setAssessmentError] = useState<string | null>(null);
  const [sectionCommentary, setSectionCommentary] = useState<Record<string, string>>({});

  // Validation
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  // AI-extracted team members
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [loadingTeamMembers, setLoadingTeamMembers] = useState(false);

  // Debug: Log assessment changes
  useEffect(() => {
    console.log('📊 Assessment state changed:', {
      hasAssessment: !!assessment,
      status: assessment?.status,
      hasData: !!assessment?.assessment_data,
      hasSections: !!assessment?.assessment_data?.sections,
      sectionsCount: assessment?.assessment_data?.sections?.length
    });
  }, [assessment]);

  // Trigger both metadata extraction and assessment when all documents are ready
  useEffect(() => {
    const allReady = uploadedFiles.every(f => f.status === 'ready');
    const hasFiles = uploadedFiles.length > 0;
    
    if (allReady && hasFiles && submissionId) {
      // Trigger metadata extraction if not already done
      if (!extractingMetadata && !extractedMetadata) {
        triggerMetadataExtraction();
      }
      
      // Trigger completeness assessment if not already done
      if (!assessing && !assessment) {
        triggerCompletenessAssessment();
      }
    }
  }, [uploadedFiles, submissionId, extractingMetadata, extractedMetadata, assessing, assessment]);

  // Fetch program data and create/fetch draft submission on mount
  useEffect(() => {
    const initPage = async () => {
      if (!programId) {
        console.error('No program ID provided');
        setLoadingSubmission(false);
        setLoadingProgram(false);
        return;
      }

      try {
        // Fetch program data
        setLoadingProgram(true);
        const programData = await programsAPI.get(parseInt(programId));
        setProgram(programData);
      } catch (error) {
        console.error('Failed to fetch program:', error);
      } finally {
        setLoadingProgram(false);
      }

      try {
        // Create or fetch draft submission
        setLoadingSubmission(true);
        const result = await submissionService.getOrCreateDraft(parseInt(programId));
        setSubmissionId(result.submission.id);
        console.log(result.created ? 'Created new draft submission' : 'Using existing draft submission', result.submission.id);
      } catch (error) {
        console.error('Failed to create/fetch submission:', error);
      } finally {
        setLoadingSubmission(false);
      }
    };

    initPage();
  }, [programId]);

  const fetchTeamMembers = async () => {
    if (!submissionId) return;
    
    setLoadingTeamMembers(true);
    try {
      const response = await submissionService.getTeamMembers(submissionId);
      // Combine extracted and manual members
      const allMembers = [...response.extracted_members, ...response.manual_members];
      setTeamMembers(allMembers);
      console.log(`✅ Loaded ${allMembers.length} team members (${response.extracted_count} extracted)`);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoadingTeamMembers(false);
    }
  };

  const triggerMetadataExtraction = async () => {
    if (!submissionId) return;
    
    setExtractingMetadata(true);
    setMetadataError(null);
    
    try {
      // Trigger extraction
      await submissionService.extractMetadata(submissionId);
      
      // Start polling for results
      submissionService.pollMetadata(
        submissionId,
        (data) => {
          setExtractedMetadata(data);
          setExtractingMetadata(false);
          setShowCompanyInfo(true);
          
          // Auto-fill form fields (but allow user to edit)
          if (data) {
            if (data.company_name) setCompanyName(data.company_name);
            if (data.website) setWebsite(data.website);
            if (data.company_address) setCompanyAddress(data.company_address);
            if (data.contact_name) setContactName(data.contact_name);
            if (data.contact_email) setContactEmail(data.contact_email);
            if (data.contact_phone) setContactPhone(data.contact_phone);
            if (data.linkedin_url) setLinkedinUrl(data.linkedin_url);
          }
          
          // Fetch team members from database
          fetchTeamMembers();
        },
        60,  // max attempts (2 minutes)
        2000 // 2 seconds interval
      ).catch((error) => {
        console.error('Metadata extraction polling failed:', error);
        setExtractingMetadata(false);
        setShowCompanyInfo(true); // Show form anyway so user can fill manually
        setMetadataError('AI extraction timed out. Please fill in the information manually.');
      });
    } catch (error) {
      console.error('Failed to trigger metadata extraction:', error);
      setExtractingMetadata(false);
      setShowCompanyInfo(true);
      setMetadataError('AI extraction failed. Please fill in the information manually.');
    }
  };

  const triggerCompletenessAssessment = async () => {
    if (!submissionId) return;

    // Don't trigger if already assessing or if we have a completed assessment
    if (assessing || (assessment && assessment.status === 'completed')) {
      console.log('⏭️ Skipping assessment trigger (already in progress or completed)');
      return;
    }

    setAssessing(true);
    setAssessmentError(null);

    try {
      // Trigger assessment
      const triggerResponse = await submissionService.assessCompleteness(submissionId);
      console.log('🚀 Assessment trigger response:', triggerResponse);

      // Start polling for results
      let attempts = 0;
      const maxAttempts = 60; // 2 minutes
      const pollInterval = setInterval(async () => {
        try {
          attempts++;
          const result = await submissionService.getCompleteness(submissionId);

          if (result.status === 'completed' && result.data) {
            console.log('✅ Assessment completed, data:', result.data);
            console.log('Assessment data structure:', {
              status: result.data.status,
              hasAssessmentData: !!result.data.assessment_data,
              hasSections: !!result.data.assessment_data?.sections,
              sectionsCount: result.data.assessment_data?.sections?.length
            });
            setAssessment(result.data);
            setAssessing(false);
            clearInterval(pollInterval);
          } else if (result.status === 'failed') {
            setAssessing(false);
            setAssessmentError(result.error_message || 'Assessment failed');
            clearInterval(pollInterval);
          } else if (attempts >= maxAttempts) {
            setAssessing(false);
            setAssessmentError('Assessment timed out. Please try again.');
            clearInterval(pollInterval);
          }
          // else continue polling (still processing or pending)
        } catch (error) {
          console.error('Polling error:', error);
          clearInterval(pollInterval);
          setAssessing(false);
          setAssessmentError('Failed to retrieve assessment results.');
        }
      }, 2000);
    } catch (error: any) {
      console.error('Failed to trigger assessment:', error);
      setAssessing(false);
      
      // More friendly error messages
      if (error?.message?.includes('400')) {
        setAssessmentError('Documents are still processing. Please wait a moment and try again.');
      } else if (error?.message?.includes('500')) {
        setAssessmentError('Server error. Our team has been notified. You can continue with your submission.');
      } else {
        setAssessmentError('Failed to start assessment. You can still submit your application.');
      }
    }
  };

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleFiles = useCallback(async (files: FileList) => {
    // Ensure we have a submission ID
    if (!submissionId) {
      console.error('No submission ID available yet');
      return;
    }

    setProcessing(true);
    
    // Create temp file records for UI
    const newFiles: UploadedFile[] = Array.from(files).map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      name: file.name,
      size: `${(file.size / 1024 / 1024).toFixed(2)} MB`,
      type: file.name.split('.').pop() || 'unknown',
      status: 'uploading' as const,
    }));

    setUploadedFiles((prev) => [...prev, ...newFiles]);

    // Upload files to backend
    const uploadPromises = Array.from(files).map(async (file, index) => {
      const tempId = newFiles[index].id;
      
      try {
        // Upload to backend using the submission we created/fetched
        const uploadedDoc = await documentService.uploadDocument(file, submissionId);
        
        // Update with backend document ID
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === tempId
              ? {
                  ...f,
                  id: uploadedDoc.id,
                  documentId: uploadedDoc.id,
                  status: uploadedDoc.processing_status,
                }
              : f
          )
        );
        
        // Poll for status updates
        documentService.pollDocumentStatus(
          uploadedDoc.id,
          (status: DocumentStatus) => {
            setUploadedFiles((prev) =>
              prev.map((f) =>
                f.documentId === uploadedDoc.id
                  ? {
                      ...f,
                      status: status.processing_status,
                      error: status.processing_error,
                    }
                  : f
              )
            );
          }
        ).catch((error) => {
          console.error('Polling error:', error);
          setUploadedFiles((prev) =>
            prev.map((f) =>
              f.documentId === uploadedDoc.id
                ? {
                    ...f,
                    status: 'error' as const,
                    error: 'Failed to poll status',
                  }
                : f
            )
          );
        });
        
      } catch (error) {
        console.error('Upload error:', error);
        setUploadedFiles((prev) =>
          prev.map((f) =>
            f.id === tempId
              ? {
                  ...f,
                  status: 'error' as const,
                  error: error instanceof Error ? error.message : 'Upload failed',
                }
              : f
          )
        );
      }
    });

    await Promise.all(uploadPromises);
    setProcessing(false);
  }, [submissionId]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFiles(e.dataTransfer.files);
    }
  }, [handleFiles]);

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      handleFiles(e.target.files);
    }
  };

  const removeFile = async (fileId: string | number) => {
    const file = uploadedFiles.find((f) => f.id === fileId);
    
    // If file has been uploaded to backend, delete it
    if (file?.documentId) {
      try {
        await documentService.deleteDocument(file.documentId);
      } catch (error) {
        console.error('Failed to delete document:', error);
      }
    }
    
    setUploadedFiles((prev) => prev.filter((f) => f.id !== fileId));
  };


  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    // Required fields
    if (!companyName.trim()) {
      errors.companyName = 'Company name is required';
    }
    if (!contactName.trim()) {
      errors.contactName = 'Contact name is required';
    }
    if (!contactEmail.trim()) {
      errors.contactEmail = 'Contact email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactEmail)) {
      errors.contactEmail = 'Invalid email format';
    }
    if (!website.trim()) {
      errors.website = 'Company website is required';
    }
    if (!companyAddress.trim()) {
      errors.companyAddress = 'Company address is required';
    }
    if (uploadedFiles.length === 0) {
      errors.documents = 'At least one document is required';
    }

    // Check if any files are still processing
    const processingFiles = uploadedFiles.filter(
      (f) => f.status === 'uploading' || f.status === 'processing'
    );
    if (processingFiles.length > 0) {
      errors.documents = `${processingFiles.length} document(s) are still processing. Please wait.`;
    }

    // Check if AI processes are still running
    if (extractingMetadata) {
      errors.ai = 'AI is still extracting company information. Please wait.';
    }
    if (assessing) {
      errors.ai = 'AI is still assessing requirements. Please wait.';
    }

    // Check commentary for non-high-confidence sections
    if (assessment && assessment.assessment_data) {
      const sectionsNeedingAttention = assessment.assessment_data.sections.filter(
        (section) => section.confidence !== 'high' || !section.is_addressed
      );

      const missingCommentary = sectionsNeedingAttention.filter(
        (section) => !sectionCommentary[section.section_title]?.trim()
      );

      if (missingCommentary.length > 0) {
        errors.assessment = `Please provide commentary for ${missingCommentary.length} section(s) that need attention.`;
      }
    }

    setValidationErrors(errors);

    // Scroll to first error
    if (Object.keys(errors).length > 0) {
      const firstErrorField = Object.keys(errors)[0];
      const element = document.querySelector(`[data-field="${firstErrorField}"]`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    return Object.keys(errors).length === 0;
  };

  const handleSubmitApplication = async () => {
    if (!submissionId) {
      console.error('No submission ID available');
      return;
    }

    // Validate form
    if (!validateForm()) {
      return;
    }

    setProcessing(true);

    try {
      // Save commentary to metadata
      await submissionService.update(submissionId, {
        submission_contact_name: contactName,
        submission_contact_email: contactEmail,
        submission_contact_phone: contactPhone,
        metadata: {
          company_name: companyName,
          company_address: companyAddress,
          linkedin_url: linkedinUrl,
          website: website,
          user_commentary: sectionCommentary,
        },
      });

      // Submit the draft
      await submissionService.submitDraft(submissionId, {
        submission_contact_name: contactName,
        submission_contact_email: contactEmail,
        submission_contact_phone: contactPhone,
        metadata: {
          company_name: companyName,
          company_address: companyAddress,
          linkedin_url: linkedinUrl,
          website: website,
          user_commentary: sectionCommentary,
        },
      });

      // Success! Navigate to the submissions list with a success state
      navigate('/submit', { 
        state: { 
          successMessage: `Application submitted successfully! Your submission is now under review.`,
          submittedId: submissionId 
        } 
      });
    } catch (error) {
      console.error('Failed to submit:', error);
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to submit application. Please try again.';
      alert(errorMessage);
      setProcessing(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!submissionId) {
      console.error('No submission ID available');
      return;
    }

    try {
      // Update the draft with current form data
      await submissionService.update(submissionId, {
        submission_contact_name: contactName,
        submission_contact_email: contactEmail,
        submission_contact_phone: contactPhone,
        metadata: {
          company_name: companyName,
          company_address: companyAddress,
          linkedin_url: linkedinUrl,
          website: website,
        },
      });

      // Navigate back to submissions list
      navigate('/submit');
    } catch (error) {
      console.error('Failed to save draft:', error);
      alert('Failed to save draft. Please try again.');
    }
  };

  const getFileIcon = () => {
    return <FileText className="w-5 h-5 text-primary" />;
  };

  const getStatusIcon = (status: UploadedFile['status']) => {
    switch (status) {
      case 'uploading':
        return (
          <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" />
        );
      case 'processing':
        return <Sparkles className="w-4 h-4 text-primary animate-pulse" />;
      case 'ready':
        return <CheckCircle className="w-4 h-4 text-success" />;
      case 'error':
        return <AlertCircle className="w-4 h-4 text-destructive" />;
    }
  };

  // Show loading while fetching program and creating submission
  if (loadingProgram || loadingSubmission) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">
            {loadingProgram ? 'Loading program...' : 'Initializing submission...'}
          </p>
        </div>
      </div>
    );
  }

  // Show error if program not found
  if (!program) {
    return (
      <div className="h-full flex items-center justify-center bg-background">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
          <p className="text-foreground text-xl font-semibold mb-2">Program not found</p>
          <p className="text-muted-foreground mb-4">
            The program you're trying to submit to could not be found.
          </p>
          <Button onClick={() => navigate('/submit')}>Back to Submissions</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-background">
      <div className="max-w-7xl mx-auto px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate('/submit')}
            className="mb-4 -ml-3"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Submissions
          </Button>
          <h1 className="text-4xl font-bold text-foreground mb-2">
            New Submission
          </h1>
          <p className="text-base text-muted-foreground">{program.name}</p>
          {program.description && (
            <p className="text-sm text-muted-foreground/80 mt-2">
              {program.description}
            </p>
          )}
        </div>

        <div className="max-w-4xl mx-auto space-y-6">
            {/* File Upload Area */}
            <Card className="p-6" data-field="documents">
              <h2 className="text-xl font-bold text-card-foreground mb-4">
                Upload Documents *
              </h2>

              <div
                className={`border-2 border-dashed rounded-lg p-12 text-center transition-colors ${
                  dragActive
                    ? 'border-primary bg-primary/10'
                    : validationErrors.documents
                    ? 'border-destructive bg-destructive/10'
                    : 'border-border'
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <Upload className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-foreground mb-2">
                  Drag and drop files here
                </p>
                <p className="text-sm text-muted-foreground mb-4">
                  or click to browse
                </p>
                <input
                  type="file"
                  multiple
                  onChange={handleFileInput}
                  className="hidden"
                  id="file-upload"
                />
                <label htmlFor="file-upload">
                  <Button variant="outline" size="sm" asChild>
                    <span className="cursor-pointer">
                      Choose Files
                    </span>
                  </Button>
                </label>
              </div>

              {validationErrors.documents && (
                <div className="mt-4 p-3 bg-destructive/10 rounded-lg border border-destructive/30">
                  <p className="text-sm text-destructive-foreground">{validationErrors.documents}</p>
                </div>
              )}

              {/* Uploaded Files */}
              {uploadedFiles.length > 0 && (
                <div className="mt-6 space-y-3">
                  {uploadedFiles.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 bg-muted rounded-lg border border-border"
                    >
                      <div className="flex items-center gap-3 flex-1">
                        {getFileIcon()}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">
                            {file.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{file.size}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(file.status)}
                          <span className="text-xs text-muted-foreground capitalize">
                            {file.status}
                          </span>
                        </div>
                      </div>
                      {file.status === 'ready' && (
                        <button
                          onClick={() => removeFile(file.id)}
                          className="ml-3 text-muted-foreground hover:text-destructive"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {(processing || extractingMetadata) && (
                <div className="mt-6 p-4 bg-primary/10 rounded-lg border border-primary/30">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                    <div>
                      <p className="text-sm font-medium text-primary-foreground">
                        {extractingMetadata 
                          ? 'AI is extracting company information...'
                          : 'AI is analyzing your documents...'
                        }
                      </p>
                      <p className="text-xs text-primary-foreground/80 mt-1">
                        {extractingMetadata
                          ? 'This will auto-fill your company details in a moment'
                          : 'Extracting team information, technical specifications, and key details'
                        }
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {metadataError && (
                <div className="mt-6 p-4 bg-warning/10 rounded-lg border border-warning/30">
                  <div className="flex items-center gap-3">
                    <AlertCircle className="w-5 h-5 text-warning" />
                    <p className="text-sm text-warning-foreground">
                      {metadataError}
                    </p>
                  </div>
                </div>
              )}
            </Card>

            {/* Company Information Form - Only show after extraction completes or times out */}
            {showCompanyInfo && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-xl font-bold text-card-foreground">
                    Company Information
                  </h2>
                  {extractedMetadata && (
                    <Badge variant="info">
                      <Sparkles className="w-3 h-3 mr-1" />
                      AI-Extracted
                    </Badge>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2" data-field="companyName">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Company Name *
                    </label>
                    <Input
                      value={companyName}
                      onChange={(e) => {
                        setCompanyName(e.target.value);
                        if (validationErrors.companyName) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.companyName;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="Your company name"
                      className={validationErrors.companyName ? 'border-red-500' : ''}
                    />
                    {validationErrors.companyName && (
                      <p className="text-sm text-destructive-foreground mt-1">{validationErrors.companyName}</p>
                    )}
                  </div>
                  <div data-field="contactName">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Primary Contact Name *
                    </label>
                    <Input
                      value={contactName}
                      onChange={(e) => {
                        setContactName(e.target.value);
                        if (validationErrors.contactName) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.contactName;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="John Doe"
                      className={validationErrors.contactName ? 'border-red-500' : ''}
                    />
                    {validationErrors.contactName && (
                      <p className="text-sm text-destructive-foreground mt-1">{validationErrors.contactName}</p>
                    )}
                  </div>
                  <div data-field="contactEmail">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Contact Email *
                    </label>
                    <Input
                      type="email"
                      value={contactEmail}
                      onChange={(e) => {
                        setContactEmail(e.target.value);
                        if (validationErrors.contactEmail) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.contactEmail;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="john@company.com"
                      className={validationErrors.contactEmail ? 'border-red-500' : ''}
                    />
                    {validationErrors.contactEmail && (
                      <p className="text-sm text-destructive-foreground mt-1">{validationErrors.contactEmail}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Phone Number
                    </label>
                    <Input
                      type="tel"
                      value={contactPhone}
                      onChange={(e) => setContactPhone(e.target.value)}
                      placeholder="+1 (555) 123-4567"
                    />
                  </div>
                  <div data-field="website">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Company Website *
                    </label>
                    <Input
                      value={website}
                      onChange={(e) => {
                        setWebsite(e.target.value);
                        if (validationErrors.website) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.website;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="https://company.com"
                      className={validationErrors.website ? 'border-red-500' : ''}
                    />
                    {validationErrors.website && (
                      <p className="text-sm text-destructive-foreground mt-1">{validationErrors.website}</p>
                    )}
                  </div>
                  <div className="col-span-2" data-field="companyAddress">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      Company Address *
                    </label>
                    <Input
                      value={companyAddress}
                      onChange={(e) => {
                        setCompanyAddress(e.target.value);
                        if (validationErrors.companyAddress) {
                          setValidationErrors((prev) => {
                            const newErrors = { ...prev };
                            delete newErrors.companyAddress;
                            return newErrors;
                          });
                        }
                      }}
                      placeholder="123 Main St, City, State, ZIP"
                      className={validationErrors.companyAddress ? 'border-red-500' : ''}
                    />
                    {validationErrors.companyAddress && (
                      <p className="text-sm text-destructive-foreground mt-1">{validationErrors.companyAddress}</p>
                    )}
                  </div>
                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-card-foreground mb-2">
                      LinkedIn Company URL
                    </label>
                    <Input
                      value={linkedinUrl}
                      onChange={(e) => setLinkedinUrl(e.target.value)}
                      placeholder="https://linkedin.com/company/..."
                    />
                  </div>
                </div>
              </Card>
            )}

            {/* Requirements Assessment */}
            {assessing && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-primary animate-pulse" />
                  <h2 className="text-xl font-bold text-card-foreground">
                    Requirements Assessment
                  </h2>
                </div>
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                  <p className="text-sm text-muted-foreground">
                    AI is analyzing your documents against program requirements...
                  </p>
                </div>
              </Card>
            )}

            {assessmentError && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <AlertTriangle className="w-5 h-5 text-warning" />
                  <h2 className="text-xl font-bold text-card-foreground">
                    Requirements Assessment
                  </h2>
                </div>
                <div className="bg-warning/10 border border-warning/30 rounded-lg p-4">
                  <p className="text-sm text-warning-foreground">{assessmentError}</p>
                </div>
              </Card>
            )}

            {assessment && assessment.status === 'completed' && assessment.assessment_data && assessment.assessment_data.sections && (
              <Card className="p-6" data-field="assessment">
                <div className="flex items-center gap-2 mb-4">
                  <CheckCircle className="w-5 h-5 text-success" />
                  <h2 className="text-xl font-bold text-card-foreground">
                    Requirements Assessment
                  </h2>
                </div>

                <div className="mb-6 p-4 bg-primary/10 border border-primary/30 rounded-lg">
                  <p className="text-sm text-primary-foreground mb-2">
                    <strong>Overall Completeness:</strong> Your documents address{' '}
                    {Math.round((assessment.overall_completeness || 0) * 100)}% of the requirements
                  </p>
                  <p className="text-xs text-primary-foreground/80">
                    Sections marked as Medium or Low Confidence require additional commentary explaining how you will address missing information.
                  </p>
                </div>

                {validationErrors.assessment && (
                  <div className="mb-4 p-4 bg-destructive/10 border border-destructive/30 rounded-lg">
                    <p className="text-sm text-destructive-foreground">{validationErrors.assessment}</p>
                  </div>
                )}

                <div className="space-y-4">
                  {assessment.assessment_data.sections.map((section: any, idx: number) => {
                    const needsAttention = section.confidence !== 'high' || !section.is_addressed;
                    const needsCommentary = needsAttention;

                    return (
                      <div
                        key={idx}
                        id={`section-${section.section_title.replace(/\s/g, '-')}`}
                        className={`p-4 rounded-lg border-2 ${
                          needsAttention
                            ? sectionCommentary[section.section_title]?.trim()
                              ? 'border-success/30 bg-success/10'
                              : 'border-warning/30 bg-warning/10'
                            : 'border-border bg-muted'
                        }`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <h3 className="font-semibold text-card-foreground">
                            {section.section_title}
                            {needsCommentary && <span className="text-destructive ml-1">*</span>}
                          </h3>
                          <Badge
                            variant={
                              section.confidence === 'high'
                                ? 'approved'
                                : section.confidence === 'medium'
                                ? 'info'
                                : 'warning'
                            }
                          >
                            {section.confidence === 'high' && 'High Confidence'}
                            {section.confidence === 'medium' && 'Medium Confidence'}
                            {section.confidence === 'low' && 'Low Confidence'}
                            {!section.is_addressed && 'Not Addressed'}
                          </Badge>
                        </div>

                        {section.notes && (
                          <p className="text-sm text-card-foreground/80 mb-3">{section.notes}</p>
                        )}

                        {section.missing_info && section.missing_info.length > 0 && (
                          <div className="mb-3 p-3 bg-card rounded border border-border">
                            <p className="text-xs font-semibold text-card-foreground mb-2">
                              Missing Information:
                            </p>
                            <ul className="text-xs text-muted-foreground list-disc list-inside space-y-1">
                              {section.missing_info.map((info: string, i: number) => (
                                <li key={i}>{info}</li>
                              ))}
                            </ul>
                          </div>
                        )}

                        {needsCommentary && (
                          <div className="mt-3">
                            <label className="block text-xs font-medium text-card-foreground mb-2">
                              Your Response {needsCommentary && <span className="text-destructive">*</span>}
                            </label>
                            <textarea
                              value={sectionCommentary[section.section_title] || ''}
                              onChange={(e) => {
                                setSectionCommentary({
                                  ...sectionCommentary,
                                  [section.section_title]: e.target.value,
                                });
                                // Clear validation error when user starts typing
                                if (validationErrors.assessment && e.target.value.trim()) {
                                  setValidationErrors((prev) => {
                                    const newErrors = { ...prev };
                                    delete newErrors.assessment;
                                    return newErrors;
                                  });
                                }
                              }}
                              rows={3}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-ring bg-background text-foreground ${
                                needsCommentary && !sectionCommentary[section.section_title]?.trim()
                                  ? 'border-warning'
                                  : 'border-input'
                              }`}
                              placeholder={
                                needsAttention
                                  ? 'Required: Explain how you will address the missing information or why it is not applicable...'
                                  : 'Provide additional context or explain how you will address missing information...'
                              }
                            />
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </Card>
            )}

            {/* Team Members Section */}
            {showCompanyInfo && (
              <Card className="p-6">
                <div className="flex items-center gap-2 mb-4">
                  <Sparkles className="w-5 h-5 text-primary" />
                  <h2 className="text-xl font-bold text-card-foreground">
                    Team Members
                  </h2>
                </div>

                {extractingMetadata || loadingTeamMembers ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
                    <p className="text-sm text-muted-foreground">
                      {extractingMetadata ? 'Extracting team information from your documents...' : 'Loading team members...'}
                    </p>
                  </div>
                ) : teamMembers.length > 0 ? (
                  <div className="space-y-3">
                    {teamMembers.map((member, idx) => (
                      <div
                        key={member.id || idx}
                        className="p-4 bg-muted rounded-lg border border-border"
                      >
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-medium text-card-foreground">
                            {member.name}
                          </p>
                          {member.extracted_from_document && (
                            <Badge variant="info" className="text-xs">
                              <Sparkles className="w-3 h-3 mr-1" />
                              AI Extracted
                            </Badge>
                          )}
                        </div>
                        {member.role && (
                          <p className="text-sm text-muted-foreground mb-1">{member.role}</p>
                        )}
                        {member.email && (
                          <p className="text-xs text-muted-foreground/80 mb-1">{member.email}</p>
                        )}
                        {member.linkedin_url && (
                          <a
                            href={member.linkedin_url.startsWith('http') ? member.linkedin_url : `https://${member.linkedin_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-xs text-primary hover:underline inline-block"
                          >
                            View LinkedIn Profile →
                          </a>
                        )}
                        {member.bio && (
                          <p className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                            {member.bio}
                          </p>
                        )}
                      </div>
                    ))}
                    <p className="text-xs text-muted-foreground text-center mt-4">
                      {teamMembers.filter(m => m.extracted_from_document).length} of {teamMembers.length} team members were extracted from your documents
                    </p>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-sm text-muted-foreground">
                      No team members found in documents. Team members can be added to your vendor profile after submission.
                    </p>
                  </div>
                )}
              </Card>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <Button
                size="lg"
                className="w-full"
                onClick={handleSubmitApplication}
                disabled={processing || !showCompanyInfo || extractingMetadata || assessing}
                isLoading={processing}
              >
                {!processing && 'Submit Application'}
              </Button>
              <Button variant="secondary" size="lg" className="w-full" onClick={handleSaveDraft}>
                Save as Draft
              </Button>
            </div>
        </div>
      </div>
    </div>
  );
};

export default NewSubmission;

